import React, { useEffect, useState } from "react";
import { Modal, Form, Select, message } from "antd";
import { getBudgetOptions } from "@/pages/campaignCreate/components/budgetBidding/config";
import { useModalStore } from "@/hooks/useModalStore";
import {
  getCreateSubscribeUrl,
  getUpdateSubscribeUrl,
  getCancelSubscribeUrl,
} from "@/api/payment";
import { usePaymentStore } from "@/hooks/usePaymentStore";
import dayjs from "dayjs";
import { normalizePriceDollar } from "@/utils/normalizePrice";
import { useQuery } from "@/hooks/useQuery";
import { useTranslation } from "react-i18next";

interface PlanModalProps {}

const timeFormat = (time: string) => {
  return dayjs(time).format("YYYY-MM-DD");
};
const ModalUpdatePlan: React.FC<PlanModalProps> = () => {
  const { t } = useTranslation();
  //
  const campaignId = useQuery("campaignId");
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<any[]>([]);

  const {
    upgradePlanModalVisible,
    setUpgradePlanModalVisible,
    updatePlanType,
    updatePlanBudgetLimitMonthly,
    setUpdatePlanBudgetLimitMonthly,
    updatePlanBudgetLimitMonthlyIsYear,
    updatePlanController,
  } = useModalStore();
  const { walletDetail, reQueryWalletData } = usePaymentStore();

  const handleSubmit = async () => {
    setLoading(true);
    const currentUrl = window.location.href;
    const [baseUrl, hash] = currentUrl.split("#");
    const url = new URL(baseUrl);

    let finalUrl;
    if (hash?.includes("/plan")) {
      // 如果已经有 #/plan，把查询参数加在hash后面
      if (hash.includes("/plan?")) {
        // 需要把hash后面的查询参数也补充上
        finalUrl = `${baseUrl}#${hash}&ahaTo=subscript`;
      } else {
        finalUrl = `${baseUrl}#${hash}?ahaTo=subscript`;
      }
    } else {
      // 如果没有 #/plan，把查询参数加在主URL上
      url.searchParams.append("ahaTo", "subscript");
      finalUrl = url.toString();
    }

    const getUrlFunction = (params: any) => {
      if (updatePlanType === "create") {
        return getCreateSubscribeUrl(params);
      } else if (updatePlanType === "upgrade") {
        return getUpdateSubscribeUrl(params);
      } else if (updatePlanType === "downgrade") {
        return getUpdateSubscribeUrl(params);
      } else if (updatePlanType === "cancel") {
        return getCancelSubscribeUrl(params);
      } else {
        console.error(t("components.modalUpdatePlan.unknownError"));
        message.error(t("components.modalUpdatePlan.unknownError"));
      }
    };

    try {
      const res = await getUrlFunction({
        plan: updatePlanBudgetLimitMonthly,
        newPlan: updatePlanBudgetLimitMonthly,
        url: finalUrl,
        interval: updatePlanBudgetLimitMonthlyIsYear ? "year" : "month",
      });

      if (res.message) {
        message.error(res.message);
        setLoading(false);
        return;
      }

      // if (updatePlanType === "create") {
      //   message.success("Subscription created successfully");
      // }
      if (updatePlanType === "cancel") {
        message.success(t("components.modalUpdatePlan.subscriptionCanceledSuccessfully"));
      }
      if (updatePlanType === "downgrade") {
        message.success(t("components.modalUpdatePlan.subscriptionDowngradedSuccessfully"));
      }
      if (updatePlanType === "upgrade") {
        message.success(t("components.modalUpdatePlan.subscriptionUpgradedSuccessfully"));
      }

      if (
        updatePlanType === "cancel" ||
        updatePlanType === "downgrade" ||
        updatePlanType === "upgrade"
      ) {
        reQueryWalletData();
        setUpgradePlanModalVisible(false, "", "");
      }
      console.log(res);
      if (res?.url) {
        window.location.href = res.url;
      }
    } catch (error) {
      message.error(`${error}`);
    } finally {
      setLoading(false);
    }
  };

  const getContent = () => {
    const hasSubscription = walletDetail?.planData?.hasSubscription;
    const billedMonthly = walletDetail?.planData?.billedMonthly;
    // 1. 提示试用 发布活动：You haven't subscribed to any plan yet. Please subscribe to release campaign. Enjoy a 14-day free trial for your first campaign.
    // 2. 升级订阅 发布活动：
    //     `Your current campaign budget has exceeded your remaining influencer quota.
    // Would you like to upgrade your plan to {minimum_applicable_plan} and proceed with publishing this campaign?`
    // ~~3. 提示试用 创建订阅：~~
    //
    // 4. 升级订阅 setting：Please select the plan you would like to upgrade to. The upgrade will take effect immediately.
    // 5. 降级订阅 setting：Please confirm that you would like to downgrade your plan. The change will take effect on {effective_date}.
    // 6. 创建订阅 setting：✅
    // 6.1 试用 创建订阅 setting:✅
    // 7. 取消订阅 setting：✅
    //
    // ~~8. 提示试用 通过达人：~~
    // 9. 升级订阅 通过达人：

    if (!hasSubscription && updatePlanController === "releaseCampaign") {
      return (
        <div className="">
          {t("components.modalUpdatePlan.employmentPricingModelMessage")}
        </div>
      );
    } else if (hasSubscription && updatePlanController === "releaseCampaign") {
      return (
        <div className="flex flex-col gap-4">
          <div className="">
            {t("components.modalUpdatePlan.campaignBudgetExceededMessage")}
          </div>
          <div className="">
            {t("components.modalUpdatePlan.upgradePlanQuestion", { budget: <strong>{normalizePriceDollar(updatePlanBudgetLimitMonthly || 0)}</strong> })}
          </div>
        </div>
      );
    } else if (
      updatePlanController === "setting" &&
      updatePlanType === "create"
    ) {
      return (
        <div className="">
          {t("components.modalUpdatePlan.chooseMonthlyBudgetMessage")}
        </div>
      );
    } else if (
      updatePlanController === "setting" &&
      updatePlanType === "upgrade"
    ) {
      return (
        <div className="">
          {t("components.modalUpdatePlan.selectUpgradePlanMessage")}
        </div>
      );
    } else if (
      updatePlanController === "setting" &&
      updatePlanType === "downgrade"
    ) {
      return (
        <div className="">
          {t("components.modalUpdatePlan.confirmDowngradeMessage", { date: timeFormat(billedMonthly) })}
        </div>
      );
    } else if (
      updatePlanController === "setting" &&
      updatePlanType === "cancel"
    ) {
      return (
        <div className="">
          {t("components.modalUpdatePlan.subscriptionCancelMessage")}
        </div>
      );
    } else if (
      updatePlanController === "confirmOrder" &&
      updatePlanType === "create"
    ) {
      return (
        <div className="">
          {t("components.modalUpdatePlan.selectCreatePlanMessage")}
        </div>
      );
    } else {
      return (
        <div className="">
          {/* 默认值 */}
          {t("components.modalUpdatePlan.defaultMessage")}
        </div>
      );
    }
  };

  const getOkText = () => {
    // 取消弹窗 主按钮是关闭弹窗
    return updatePlanType === "create"
      ? t("components.modalUpdatePlan.purchase")
      : updatePlanType === "downgrade"
      ? t("components.modalUpdatePlan.downgradeSubscription")
      : updatePlanType === "cancel"
      ? t("components.modalUpdatePlan.proceed")
      : t("components.modalUpdatePlan.upgradeSubscription");
  };
  const getCancelText = () => {
    // 取消弹窗 主按钮是关闭弹窗
    return updatePlanController === "releaseCampaign" ? t("components.modalUpdatePlan.notNow") : t("components.modalUpdatePlan.cancel");
  };
  const getTitle = () => {
    return updatePlanType === "create"
      ? t("components.modalUpdatePlan.purchaseYourPlan")
      : updatePlanType === "downgrade"
      ? t("components.modalUpdatePlan.downgradeSubscriptionPlan")
      : updatePlanType === "cancel"
      ? t("components.modalUpdatePlan.cancelSubscriptionPlan")
      : t("components.modalUpdatePlan.upgradeYourSubscription");
  };

  useEffect(() => {
    console.log("updatePlanType", updatePlanType);

    let currentSelectQuota = 0;
    let thisOptions = [];
    const budgetOptions = getBudgetOptions();
    if (updatePlanType === "upgrade" || updatePlanType === "create") {
      thisOptions = budgetOptions.filter(
        (item) => item.value >= (updatePlanBudgetLimitMonthly || walletDetail?.planData?.quota)
      );
      if (
        updatePlanBudgetLimitMonthly &&
        updatePlanBudgetLimitMonthly > thisOptions?.[0]?.value
      ) {
        // 那个大选哪个。
        currentSelectQuota = updatePlanBudgetLimitMonthly;
      } else {
        currentSelectQuota = thisOptions?.[0]?.value || 0;
      }
    } else if (updatePlanType === "downgrade") {
      thisOptions = budgetOptions.filter(
        (item) => item.value < walletDetail?.planData?.quota
      );
      currentSelectQuota = thisOptions?.[thisOptions.length - 1]?.value || 0;
    } else {
      thisOptions = budgetOptions;
      currentSelectQuota = walletDetail?.planData?.quota || 0;
    }
    setUpdatePlanBudgetLimitMonthly(currentSelectQuota);
    setOptions(thisOptions);
  }, [updatePlanType, walletDetail?.planData?.quota]);

  return (
    <Modal
      open={upgradePlanModalVisible}
      title={getTitle()}
      footer={(_, { OkBtn, CancelBtn }) =>
        updatePlanType !== "cancel"
          ? [<CancelBtn />, <OkBtn />]
          : [<OkBtn />, <CancelBtn />]
      }
      okText={getOkText()}
      cancelText={getCancelText()}
      onCancel={() => {
        setUpgradePlanModalVisible(false, "", "");
      }}
      onOk={() => {
        handleSubmit();
      }}
      confirmLoading={loading}
      maskClosable={false}
      destroyOnClose={true}
    >
      <div className="text-[#1c2024] text-base font-normal  leading-normal">
        <div className="mb-4">{getContent()}</div>
        <Select
          className={`
            ${updatePlanType === "cancel" ? "hidden" : "flex"}
          `}
          disabled={Boolean(campaignId) && updatePlanType === "create"}
          options={options}
          value={updatePlanBudgetLimitMonthly}
          style={{ width: "100%" }}
          suffixIcon="USD"
          onChange={(value) => {
            setUpdatePlanBudgetLimitMonthly(value);
          }}
        />
      </div>
    </Modal>
  );
};

export default ModalUpdatePlan;
